import type { MortalityLog } from '../../types/production'
import type { BiomassDataRow } from '../../utils/productionUnitTable'

import {
  buildDailyMortalityRollupKey,
  rollupMortalityLogsByLotDay,
  type DailyMortalityRollup
} from '../../utils/dailyMortalityRollup'
import { toLocalCalendarDayKey } from '../../utils/dateNormalization'
import { encodeMortalityTimeline } from '../../utils/countDayDeaths'
import { farmTimeZone } from '../../utils/farmTime'

type BiomassMortalityMetadata = {
  productionRowId?: string
  lotId?: string
  stage?: 'Active' | 'Inactive'
}

const toTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const parseBiomassMortalityMetadata = (log: MortalityLog): BiomassMortalityMetadata => {
  const metadata = log.metadata
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return {}

  const stage = toTrimmedString(metadata.stage)
  let normalizedStage: 'Active' | 'Inactive' | undefined
  if (stage === 'Inactive') normalizedStage = 'Inactive'
  else if (stage === 'Active') normalizedStage = 'Active'

  return {
    productionRowId: toTrimmedString(metadata.productionRowId),
    lotId: toTrimmedString(metadata.lotId),
    stage: normalizedStage
  }
}

const toCalendarDateKey = (value: string | undefined | null, timeZone?: string): string => {
  // Local-day keying so a ProductionRecord stored as "YYYY-MM-DDTHH:MM"
  // (datetime-local, no Z) groups with its companion MortalityLog whose
  // recordedAt is ISO Z (UTC). Without this, evening rows in behind-UTC
  // time zones split off into their own bucket and the MORT-12 trust
  // signal disagrees with what users see on the page. See
  // [docs/release/v2.1/findings.md](../../../docs/release/v2.1/findings.md)
  // row #7 for the v2.1 evidence.
  return toLocalCalendarDayKey(value, timeZone)
}

const buildRollupKeyForRow = (rowDate: string, lot: string | undefined, timeZone?: string): string => {
  return buildDailyMortalityRollupKey(toCalendarDateKey(rowDate, timeZone), String(lot ?? '').trim())
}

export const buildBiomassSamplingMortalityMetadata = (args: {
  productionRowId: string
  lotId: string
  stage: 'Active' | 'Inactive'
}): Record<string, unknown> => ({
  source: 'biomassSampling',
  productionRowId: args.productionRowId,
  lotId: args.lotId,
  stage: args.stage
})

/**
 * Find the single `MortalityLog` linked to this row for the
 * "edit-this-event" affordance. Prefers a metadata-tagged match
 * (the log captured *for* this row), then falls back to the first
 * log matching `(calendar date, lot)`.
 *
 * Note: this is intentionally 1:1. For the canonical *daily total*
 * (which sums every event on the same day for the same lot) use
 * `findAllLinkedMortalityLogsForRow` or read `row.dailyMortality`
 * after `overlayMortalityLogsOnProductionRows`.
 */
export const findLinkedMortalityLog = (
  row: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>,
  logs: MortalityLog[]
): MortalityLog | undefined => {
  const rowDateKey = toCalendarDateKey(row.date)
  if (!rowDateKey) {
    // Without a parseable row date we cannot honestly fall back; only a
    // metadata-tagged match is meaningful.
    return logs.find((log) => parseBiomassMortalityMetadata(log).productionRowId === row.id)
  }

  const rollupKey = buildRollupKeyForRow(row.date, row.lot)
  let fallbackMatch: MortalityLog | undefined

  for (const log of logs) {
    const metadata = parseBiomassMortalityMetadata(log)
    if (metadata.productionRowId === row.id) return log
    if (!fallbackMatch && buildRollupKeyForRow(log.recordedAt, log.lot, farmTimeZone(log.farmId)) === rollupKey) {
      fallbackMatch = log
    }
  }

  return fallbackMatch
}

/**
 * Return every `MortalityLog` whose `(calendar date, lot)` matches this row,
 * in input order, with the metadata-tagged log promoted to the front when one
 * exists. Used by trust-signal UI (the "derived from N events" tooltip) and
 * any consumer that needs per-event detail beyond the aggregated total.
 */
export const findAllLinkedMortalityLogsForRow = (
  row: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>,
  logs: MortalityLog[]
): MortalityLog[] => {
  const rowDateKey = toCalendarDateKey(row.date)
  if (!rowDateKey) return []

  const rollupKey = buildRollupKeyForRow(row.date, row.lot)
  const matched: MortalityLog[] = []
  let metadataMatchIndex = -1

  for (const log of logs) {
    if (buildRollupKeyForRow(log.recordedAt, log.lot, farmTimeZone(log.farmId)) !== rollupKey) continue
    const metadata = parseBiomassMortalityMetadata(log)
    if (metadataMatchIndex === -1 && metadata.productionRowId === row.id) {
      metadataMatchIndex = matched.length
    }
    matched.push(log)
  }

  if (metadataMatchIndex > 0) {
    const [primary] = matched.splice(metadataMatchIndex, 1)
    matched.unshift(primary)
  }

  return matched
}

const pickPrimaryLogForRow = (
  rowId: string,
  rollup: DailyMortalityRollup,
  logsById: Map<string, MortalityLog>
): MortalityLog | undefined => {
  // Prefer a metadata-tagged log for this row, then the most recently recorded
  // contributing log so the "edit this event" affordance points at something
  // operationally meaningful.
  for (const logId of rollup.logIds) {
    const log = logsById.get(logId)
    if (!log) continue
    if (parseBiomassMortalityMetadata(log).productionRowId === rowId) return log
  }
  for (let i = rollup.logIds.length - 1; i >= 0; i -= 1) {
    const log = logsById.get(rollup.logIds[i])
    if (log) return log
  }
  return undefined
}

/**
 * Overlay the canonical daily mortality total onto each production row.
 *
 * For every row that has at least one matching `MortalityLog` (matched by
 * `(calendar date, lot)`), the row's `dailyMortality` is replaced with the
 * sum of every matching log's `mortalityCount` (the v2.1 MORT-12 rollup
 * rule, locked 2026-06-18: events are canonical when they exist; the stored
 * `ProductionRecord.dailyMortality` becomes display-derived only).
 *
 * The row also carries:
 * - `mortalityLogId` — id of the metadata-tagged log for that row, or the
 *   most recent contributing log when no metadata-tagged log exists. Anchors
 *   the "edit this event" affordance.
 * - `mortalityLogCount` — string-encoded number of contributing logs. UI
 *   consumers parse this to render the "derived from N events" trust badge.
 *
 * Rows without matching logs are returned untouched (referential equality
 * preserved).
 */
export const overlayMortalityLogsOnProductionRows = (
  rows: BiomassDataRow[],
  logs: MortalityLog[]
): BiomassDataRow[] => {
  if (rows.length === 0 || logs.length === 0) return rows

  const rollupByKey = rollupMortalityLogsByLotDay(logs)
  if (rollupByKey.size === 0) return rows

  const logsById = new Map<string, MortalityLog>()
  for (const log of logs) {
    if (typeof log.id === 'string' && log.id) {
      logsById.set(log.id, log)
    }
  }

  let changed = false
  const nextRows = rows.map((row) => {
    const key = buildRollupKeyForRow(row.date, row.lot)
    const rollup = rollupByKey.get(key)
    if (!rollup) return row

    const primaryLog = pickPrimaryLogForRow(row.id, rollup, logsById)
    if (!primaryLog) return row

    const metadata = parseBiomassMortalityMetadata(primaryLog)
    const nextDailyMortality = String(rollup.totalCount)
    const nextLotId = row.lotId ?? metadata.lotId
    const nextStage = metadata.stage ?? row.stage
    const nextMortalityLogId = primaryLog.id
    const nextMortalityLogCount = String(rollup.eventCount)
    // Findings #203: when each death of the day was recorded, for the stock rule.
    const nextMortalityTimeline = encodeMortalityTimeline(rollup.timeline)

    if (
      row.dailyMortality === nextDailyMortality
      && row.lotId === nextLotId
      && row.stage === nextStage
      && row.mortalityLogId === nextMortalityLogId
      && row.mortalityLogCount === nextMortalityLogCount
      && row.mortalityTimeline === nextMortalityTimeline
    ) {
      return row
    }

    changed = true
    return {
      ...row,
      dailyMortality: nextDailyMortality,
      lotId: nextLotId,
      stage: nextStage,
      mortalityLogId: nextMortalityLogId,
      mortalityLogCount: nextMortalityLogCount,
      mortalityTimeline: nextMortalityTimeline
    }
  })

  return changed ? nextRows : rows
}

/**
 * Sidecar map from production-row id to the canonical event-derived mortality
 * rollup for that `(calendar date, lot)`. Returned without mutating the input
 * rows so screens that need to keep `data` as their editable source of truth
 * (notably the Database screen) can render trust signals next to the stored
 * value instead of overwriting it.
 *
 * Rows whose `(calendar date, lot)` does not match any log are omitted.
 */
export const computeMortalityRollupByRowId = (
  rows: Pick<BiomassDataRow, 'id' | 'date' | 'lot'>[],
  logs: MortalityLog[]
): Map<string, { totalDailyMortality: string; eventCount: number; primaryLogId: string | undefined }> => {
  const out = new Map<string, { totalDailyMortality: string; eventCount: number; primaryLogId: string | undefined }>()
  if (rows.length === 0 || logs.length === 0) return out

  const rollupByKey = rollupMortalityLogsByLotDay(logs)
  if (rollupByKey.size === 0) return out

  const logsById = new Map<string, MortalityLog>()
  for (const log of logs) {
    if (typeof log.id === 'string' && log.id) logsById.set(log.id, log)
  }

  for (const row of rows) {
    const key = buildRollupKeyForRow(row.date, row.lot)
    const rollup = rollupByKey.get(key)
    if (!rollup || rollup.eventCount === 0) continue
    const primary = pickPrimaryLogForRow(row.id, rollup, logsById)
    out.set(row.id, {
      totalDailyMortality: String(rollup.totalCount),
      eventCount: rollup.eventCount,
      primaryLogId: primary?.id
    })
  }

  return out
}